import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { transferGroup } from 'api/operations/groupService';
import { getProducerListByAgency } from 'api/operations/userService';
import { action, autorun, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import { ParentStore, SearchAgencyDelegate } from 'store/pages/bi-groups-dashboard/searchAgencyDelegate';
import { SelectedGroup } from 'store/pages/bi-groups-dashboard/types';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import { Producer } from 'types/userService';

interface CreationParams {
  parentStore: ParentStore;
  getSelectedGroup: () => SelectedGroup;
  closeModal: () => void;
}

interface FormFields extends DefaultFormFields {
  agencyId: FormControl<string[]>;
  aorId: FormControl<string>;
}

export default class MoveGroupModalStore {
  private readonly _rootStore: RootStore = getRootStoreInstance();
  private _parentStore: ParentStore;
  private _isAorSectionLoading = false;
  private _agencyProducers: Producer[] = [];
  private _getSelectedGroup: () => SelectedGroup;
  private _closeModal: () => void;

  private async _moveGroup(): Promise<void> {
    await runInAction(async () => {
      const currentProducer = this.agencyProducers.find((producer) => producer.id === this.form.fields.aorId.value);
      await runInAction(async () => {
        await transferGroup(
          this._getSelectedGroup().id,
          this.form.fields.agencyId.value[0] ?? '',
          currentProducer?.sponsorSpecificData?.npn || '',
          currentProducer?.sponsorSpecificData?.tin,
        );
      });
      this._closeModal();
      await this._parentStore.getInitialData();
    });
  }

  private _setAorLoading(loadingStatus: boolean): void {
    this._isAorSectionLoading = loadingStatus;
  }

  private _setAgencyProducers(producers: Producer[]): void {
    this._agencyProducers = producers;
  }

  private async _getAgencyProducers(agencyId: string): Promise<void> {
    this._setAorLoading(true);

    const producers = await getProducerListByAgency(agencyId, '');
    runInAction(() => {
      this._setAgencyProducers(producers);
      this._setAorLoading(false);
    });
  }

  public form = new FormController<FormFields>(
    { agencyId: new FormControl([], { optional: false }), aorId: new FormControl('', { optional: true }) },
    { onSubmit: this._moveGroup.bind(this) },
  );

  public searchAgencyDelegate;

  public get isAorSectionLoading(): boolean {
    return this._isAorSectionLoading;
  }

  public get agencyProducers(): Producer[] {
    return this._agencyProducers;
  }

  public get isAorOptional(): boolean {
    return this._rootStore.domainStoreV2.groupInitiationRedirect;
  }

  public resetModalData(): void {
    this.form.resetForm();
    this._agencyProducers = [];
  }

  constructor({ parentStore, getSelectedGroup, closeModal }: CreationParams) {
    this._getSelectedGroup = getSelectedGroup;
    this._closeModal = closeModal;
    this.searchAgencyDelegate = new SearchAgencyDelegate({ parentStore });
    this._parentStore = parentStore;

    makeObservable<
      MoveGroupModalStore,
      | '_isAorSectionLoading'
      | '_agencyProducers'
      | '_moveGroup'
      | '_setAorLoading'
      | '_setAgencyProducers'
      | '_getAgencyProducers'
    >(this, {
      _isAorSectionLoading: observable,
      _agencyProducers: observable,
      form: observable,
      searchAgencyDelegate: observable,
      isAorSectionLoading: computed,
      agencyProducers: computed,
      isAorOptional: computed,
      _setAorLoading: action.bound,
      _moveGroup: action.bound,
      _setAgencyProducers: action.bound,
      _getAgencyProducers: action.bound,
      resetModalData: action.bound,
    });

    reaction(
      () => this.form.fields.agencyId.value[0] ?? '',
      (agencyId) => {
        this.form.fields.aorId.overrideValue('');
        if (agencyId) {
          void this._getAgencyProducers(agencyId);
        } else {
          this._setAgencyProducers([]);
        }
      },
    );

    autorun(() => {
      if (this._agencyProducers.length > 0) {
        this.form.addFormField('aorId', new FormControl('', { optional: this.isAorOptional }));
      } else {
        this.form.addFormField('aorId', new FormControl('', { disabled: true, optional: true }));
      }
    });
  }
}
